<template>
  <div class="relative min-w-0 w-full sm:max-w-64">
    <button
      :id="triggerId"
      ref="triggerRef"
      type="button"
      :disabled="disabled"
      :aria-label="triggerLabel"
      :aria-expanded="open"
      :aria-controls="listboxId"
      :aria-busy="loading"
      aria-haspopup="listbox"
      :aria-activedescendant="activeOptionId"
      class="group inline-flex h-10 min-w-0 w-full items-center gap-2 rounded-md border border-border bg-bg px-2.5 text-left outline-2 outline-transparent outline-offset-2 transition-[background-color,border-color,box-shadow,color] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] hover:border-accent hover:bg-bg-surface focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50"
      @click="toggleListbox"
      @keydown="onTriggerKeydown"
    >
      <span class="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-accent/40 bg-accent/10 font-display text-2xs font-bold tracking-wide text-accent uppercase">
        {{ selectedGuild === undefined ? 'G' : guildInitials(selectedGuild.name) }}
      </span>
      <span class="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
        <span class="text-2xs font-semibold tracking-[0.08em] text-text-muted uppercase">{{ messages.activeGuild }}</span>
        <span class="truncate text-xs font-semibold text-text-primary">
          {{ selectedGuild?.name ?? selectorLabel }}
        </span>
      </span>
      <AppIcon
        class="shrink-0 text-text-muted transition-colors group-hover:text-accent"
        :name="open ? IconName.ChevronUp : IconName.ChevronDown"
        :size="15"
      />
    </button>

    <div
      v-if="open"
      ref="listRef"
      class="absolute right-0 top-full z-40 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border-strong bg-bg-elevated p-1.5 shadow-panel"
    >
      <div class="flex items-center justify-between border-b border-border px-2 pb-1.5 pt-1">
        <span class="text-2xs font-semibold tracking-widest text-text-muted uppercase">{{ messages.switchGuild }}</span>
        <span class="font-mono text-2xs text-text-muted">{{ availabilitySummary }}</span>
      </div>

      <div
        :id="listboxId"
        role="listbox"
        :aria-labelledby="triggerId"
        tabindex="-1"
      >
        <div v-if="loading && available.length === 0" class="px-2 py-4 text-center text-xs text-text-muted" role="status">
          {{ messages.loadingGuilds }}
        </div>
        <div v-else-if="available.length === 0" class="px-2 py-4 text-center text-xs text-text-muted" role="status">
          {{ messages.noGuilds }}
        </div>
        <div v-else class="mt-1 flex max-h-72 flex-col gap-0.5 overflow-y-auto">
          <button
            v-for="(guild, index) in available"
            :id="optionId(guild)"
            :key="guild.id"
            type="button"
            role="option"
            :aria-selected="guild.discordGuildId === modelValue"
            :class="optionClass(index, guild)"
            @click="selectGuild(guild.discordGuildId)"
            @mouseenter="highlightedIndex = index"
          >
            <span class="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-bg-surface font-display text-2xs font-bold tracking-wide text-text-secondary uppercase">
              {{ guildInitials(guild.name) }}
            </span>
            <span class="min-w-0 flex-1 text-left">
              <span class="block truncate text-xs font-semibold text-text-primary">{{ guild.name }}</span>
              <span class="block truncate text-2xs text-text-muted">{{ accessLabel(guild) }}</span>
            </span>
            <span :class="roleClass(guild)" class="shrink-0 rounded-full border px-1.5 py-0.5 text-2xs font-semibold tracking-wide uppercase">
              {{ roleLabel(guild) }}
            </span>
            <AppIcon
              v-if="guild.discordGuildId === modelValue"
              class="shrink-0 text-accent"
              :name="IconName.Shield"
              :size="14"
            />
          </button>
        </div>
      </div>

      <section
        v-if="botInviteUrl !== null || botInviteLoading || botInviteError !== null"
        :aria-label="messages.addBot"
        class="mt-1 border-t border-border px-2 pb-1 pt-2"
      >
        <a
          v-if="botInviteUrl !== null"
          :href="botInviteUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex min-h-10 w-full items-center justify-center rounded-md border border-accent bg-bg-elevated px-3 py-2 text-xs font-medium text-accent transition-[background-color,color] duration-base ease-standard hover:bg-accent hover:text-bg-base focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >{{ messages.addBot }}</a>
        <p v-else-if="botInviteLoading" class="py-2 text-center text-xs text-text-muted" role="status">{{ messages.preparingBotInvite }}</p>
        <p v-else class="py-2 text-center text-xs text-red-400" role="alert">{{ messages.botInviteFailed }}</p>
      </section>

      <section v-if="locked.length > 0" class="mt-1 border-t border-border px-2 pb-1 pt-2" :aria-labelledby="lockedGuildsHeadingId">
        <div class="flex items-center justify-between pb-1.5">
          <span :id="lockedGuildsHeadingId" class="text-2xs font-semibold tracking-widest text-text-muted uppercase">{{ messages.restrictedGuilds }}</span>
          <span class="font-mono text-2xs text-text-muted">{{ locked.length }}</span>
        </div>
        <div class="flex max-h-60 flex-col gap-1 overflow-y-auto">
          <div
            v-for="guild in locked"
            :key="guild.id"
            class="flex items-center gap-2 rounded-md border border-border bg-bg px-2 py-2"
          >
            <span class="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-bg-surface font-display text-2xs font-bold tracking-wide text-text-secondary uppercase">
              {{ guildInitials(guild.name) }}
            </span>
            <span class="min-w-0 flex-1 text-left">
              <span class="block truncate text-xs font-semibold text-text-primary">{{ guild.name }}</span>
              <span class="block truncate text-2xs text-text-muted">{{ lockedAccessLabel(guild) }}</span>
            </span>
            <AppButton
              :variant="ButtonVariant.Secondary"
              :size="ButtonSize.Sm"
              :disabled="guild.accessRequested || requestingGuildId === guild.discordGuildId"
              @click="requestAccess(guild.discordGuildId)"
            >{{ requestButtonLabel(guild) }}</AppButton>
          </div>
        </div>
        <p v-if="requestError" class="pt-2 text-xs text-red-400" role="alert">{{ requestError }}</p>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useListbox } from '@/shared/composables'
import { guildSelectorEn } from './locales/en.ts'
import { guildSelectorRu } from './locales/ru.ts'

import AppButton, { ButtonSize, ButtonVariant } from './AppButton.vue'
import AppIcon, { IconName } from './AppIcon.vue'

export interface GuildOption {
  readonly id: number
  readonly name: string
  readonly discordGuildId: string
  readonly ownerDiscordId?: string
  readonly isOfficer?: boolean
}

export interface LockedGuildOption {
  readonly id: number
  readonly name: string
  readonly discordGuildId: string
  readonly accessRequested: boolean
}

export interface GuildSelectorProps {
  readonly available: ReadonlyArray<GuildOption>
  readonly locked?: ReadonlyArray<LockedGuildOption>
  readonly modelValue: string
  readonly currentUserId?: string
  readonly requestingGuildId?: string | null
  readonly requestError?: string | null
  /** Authenticated Discord bot invite state rendered inside this menu. */
  readonly botInviteUrl?: string | null
  readonly botInviteLoading?: boolean
  readonly botInviteError?: string | null
  /**
   * `true` while the upstream guild list is being fetched. When `false` and
   * `available` is empty, the selector shows a definite empty state.
   */
  readonly loading?: boolean
}

const catalog = defineLocaleCatalog({
  en: guildSelectorEn,
  ru: guildSelectorRu,
})
const messages = useLocaleMessages(catalog)

const props = withDefaults(defineProps<GuildSelectorProps>(), {
  currentUserId: '',
  loading: false,
  locked: () => [],
  requestingGuildId: null,
  requestError: null,
  botInviteUrl: null,
  botInviteLoading: false,
  botInviteError: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', snowflake: string): void
  (e: 'request-access', snowflake: string): void
}>()

const triggerId = `guild-selector-trigger-${useId()}`
const listboxId = `guild-selector-listbox-${useId()}`
const lockedGuildsHeadingId = `guild-selector-locked-heading-${useId()}`
const selectedGuild = computed<GuildOption | undefined>(() =>
  props.available.find((guild) => guild.discordGuildId === props.modelValue),
)
const selectedIndex = computed<number>(() =>
  props.available.findIndex((guild) => guild.discordGuildId === props.modelValue),
)
const highlightedIndex = ref<number>(0)
const selectorLabel = computed<string>(() => {
  if (props.loading) return messages.value.loadingGuilds
  return props.locked.length > 0 ? messages.value.guildAccessRequired : messages.value.selectGuild
})
const availabilitySummary = computed<string>(() =>
  props.locked.length === 0
    ? messages.value.available(props.available.length)
    : messages.value.availableAndRestricted(props.available.length, props.locked.length),
)
const hasBotInviteContent = computed<boolean>(() =>
  props.botInviteUrl !== null || props.botInviteLoading || props.botInviteError !== null,
)

const {
  open,
  triggerRef,
  listRef,
  toggleListbox,
  closeListbox,
  onKeydown,
  select,
} = useListbox({
  onSelect: (snowflake) => emit('update:modelValue', snowflake),
})

// The refs are consumed by the template's `ref` bindings; keep an explicit
// read here because vue-tsc's noUnusedLocals analysis does not count those
// bindings when they come from a destructured composable result.
void triggerRef
void listRef

const disabled = computed<boolean>(() =>
  props.available.length === 0 && props.locked.length === 0 && !hasBotInviteContent.value,
)
const triggerLabel = computed<string>(() =>
  selectedGuild.value === undefined
    ? props.locked.length > 0 ? messages.value.requestGuildAccess : messages.value.selectActiveGuild
    : messages.value.activeGuildLabel(selectedGuild.value.name),
)
const activeOptionId = computed<string | undefined>(() => {
  if (!open.value) return undefined
  const option = props.available[highlightedIndex.value] ?? selectedGuild.value ?? props.available[0]
  return option === undefined ? undefined : optionId(option)
})

watch(open, (isOpen) => {
  if (isOpen) {
    highlightedIndex.value = selectedIndex.value >= 0 ? selectedIndex.value : 0
  }
})

watch(() => [props.available.length, props.locked.length, hasBotInviteContent.value] as const, ([availableLength, lockedLength, inviteAvailable]) => {
  if (availableLength === 0) {
    highlightedIndex.value = 0
    if (lockedLength === 0 && !inviteAvailable) closeListbox()
    return
  }
  highlightedIndex.value = Math.min(highlightedIndex.value, availableLength - 1)
})

function optionId(guild: GuildOption): string {
  return `guild-selector-option-${guild.id}`
}

function guildInitials(name: string): string {
  const trimmed = name.trim()
  if (trimmed === '') return 'G'
  const first = trimmed.slice(0, 1).toUpperCase()
  const separator = trimmed.indexOf(' ')
  if (separator < 0) return first
  const second = trimmed.slice(separator + 1).trim().slice(0, 1).toUpperCase()
  return second === '' ? first : `${first}${second}`
}

function isOwner(guild: GuildOption): boolean {
  return guild.ownerDiscordId !== undefined
    && guild.ownerDiscordId !== ''
    && guild.ownerDiscordId === props.currentUserId
}

function roleLabel(guild: GuildOption): string {
  if (isOwner(guild)) return messages.value.roles.owner
  if (guild.isOfficer === true) return messages.value.roles.officer
  return messages.value.roles.member
}

function roleClass(guild: GuildOption): string {
  return isOwner(guild) || guild.isOfficer === true
    ? 'border-accent/30 bg-accent/10 text-accent'
    : 'border-border bg-bg-surface text-text-muted'
}

function accessLabel(guild: GuildOption): string {
  return isOwner(guild) || guild.isOfficer === true
    ? messages.value.access.boardEditingEnabled
    : messages.value.access.viewOnly
}

function lockedAccessLabel(guild: LockedGuildOption): string {
  return guild.accessRequested ? messages.value.access.requestPending : messages.value.access.discordRoleRequired
}

function requestButtonLabel(guild: LockedGuildOption): string {
  if (guild.accessRequested) return messages.value.request.pending
  return props.requestingGuildId === guild.discordGuildId ? messages.value.request.requesting : messages.value.request.requestAccess
}

function optionClass(index: number, guild: GuildOption): string {
  const isSelected = guild.discordGuildId === props.modelValue
  const isHighlighted = index === highlightedIndex.value
  return [
    'group inline-flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left outline-2 outline-transparent outline-offset-[-2px] transition-[background-color,outline-color] duration-[120ms]',
    isHighlighted ? 'bg-accent/10 outline-accent/40' : 'hover:bg-bg-surface',
    isSelected ? 'text-text-primary' : 'text-text-secondary',
  ].join(' ')
}

function selectGuild(snowflake: string): void {
  const index = props.available.findIndex((guild) => guild.discordGuildId === snowflake)
  if (index >= 0) highlightedIndex.value = index
  select(snowflake)
}

function requestAccess(snowflake: string): void {
  emit('request-access', snowflake)
}

function onTriggerKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    onKeydown(event)
    return
  }
  if (disabled.value) return

  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    if (!open.value) {
      toggleListbox()
      return
    }
    if (props.available.length === 0) return
    const direction = event.key === 'ArrowDown' ? 1 : -1
    highlightedIndex.value = Math.min(
      props.available.length - 1,
      Math.max(0, highlightedIndex.value + direction),
    )
    return
  }

  if (event.key === 'Home' || event.key === 'End') {
    if (!open.value) return
    event.preventDefault()
    highlightedIndex.value = event.key === 'Home' ? 0 : props.available.length - 1
    return
  }

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    if (!open.value) {
      toggleListbox()
      return
    }
    const guild = props.available[highlightedIndex.value]
    if (guild !== undefined) selectGuild(guild.discordGuildId)
  }
}
</script>
